/**
 * components/forms/SmartTableFormatGuide.tsx
 *
 * Collapsible format guide displayed in the SmartTableImportModal when no file
 * has been uploaded yet. Contains an example table and LLM-friendly instructions
 * describing the exact format expected by the importer.
 *
 * Created April 2026: extracted from SmartTableImportModal to respect the 300-line
 *   component limit. Updated with Obligatoire column and compound condition examples.
 * Updated April 2026: added copy-to-clipboard button that copies a self-contained
 *   LLM-ready prompt (task preamble + spec + CSV example) so users can paste it
 *   directly into any AI assistant without reformatting.
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';

interface ExampleRow {
  intitule: string;
  question: string;
  type: string;
  description: string;
  options: string;
  condition: string;
  obligatoire: string;
}

const EXAMPLE_ROWS: ExampleRow[] = [
  { intitule: 'Situation familiale', question: 'Quelle est votre situation familiale ?', type: 'Choix unique', description: '', options: 'Célibataire, Marié(e), Pacsé(e), Divorcé(e)', condition: '', obligatoire: 'Oui' },
  { intitule: 'A des enfants', question: 'Avez-vous des enfants à charge ?', type: 'Choix unique', description: '', options: 'Oui, Non', condition: '', obligatoire: 'Oui' },
  { intitule: 'Revenus complémentaires', question: 'Quels revenus complémentaires percevez-vous ?', type: 'Choix multiple', description: '', options: 'Retraite, Chômage, RSA', condition: '', obligatoire: 'Oui' },
  { intitule: 'Pièce d\'identité', question: 'Ajoutez votre pièce d\'identité en cours de validité', type: 'Document', description: 'CNI, passeport ou titre de séjour', options: '', condition: '', obligatoire: 'Oui' },
  { intitule: 'Livret de famille', question: 'Fournissez votre livret de famille', type: 'Document', description: 'Acte de mariage ou de PACS', options: '', condition: 'Si Situation familiale = Marié(e), Pacsé(e)', obligatoire: 'Oui' },
  { intitule: 'Acte de naissance enfant', question: '', type: 'Document', description: '', options: '', condition: 'Si Situation familiale = Marié(e) ET Si A des enfants = Oui', obligatoire: 'Oui' },
  { intitule: 'Attestation CAF', question: '', type: 'Document', description: '', options: '', condition: 'Si Revenus complémentaires = Chômage OU Si A des enfants = Oui', obligatoire: 'Oui' },
  { intitule: 'Commentaires', question: '', type: 'Texte long', description: 'Informations complémentaires éventuelles', options: '', condition: '', obligatoire: 'Non' },
];

const COPYABLE_PROMPT = `Génère un fichier CSV (séparateur : virgule, encodage UTF-8) contenant la liste des champs d'un formulaire de collecte de documents et d'informations client.

RÈGLES DU FORMAT CSV :
- La première ligne doit contenir exactement ces en-têtes dans cet ordre :
  Intitulé,Question,Type,Description,Options,Condition,Obligatoire
- Toute cellule contenant une virgule doit être entourée de guillemets doubles.
- Les cellules vides sont représentées par rien entre deux virgules (ex : ,,).

DESCRIPTION DES COLONNES :

Intitulé (obligatoire)
  Nom court interne du champ. Sert de référence dans la colonne Condition.
  Exemple : Situation familiale

Type (obligatoire)
  Type du champ. Valeurs acceptées exactes (respecter la casse et les espaces) :
    Document
    Texte court
    Texte long
    Date
    Choix unique
    Choix multiple

Question (optionnel)
  Texte affiché au client. Si absent ou vide, l'Intitulé est utilisé.
  Exemple : Quelle est votre situation familiale ?

Description (optionnel)
  Texte d'aide affiché sous la question.
  Exemple : CNI, passeport ou titre de séjour

Options (requis uniquement si Type = Choix unique ou Choix multiple)
  Valeurs possibles séparées par des virgules. La cellule doit être entre guillemets doubles.
  Exemple : "Célibataire, Marié(e), Pacsé(e), Divorcé(e)"

Condition (optionnel)
  Règle d'affichage conditionnel. Le champ source doit être de type Choix unique ou Choix multiple,
  identifié par son Intitulé exact. Trois syntaxes possibles :

  Condition simple (une source, une ou plusieurs valeurs déclenchantes) :
    Si [Intitulé du champ source] = Valeur1, Valeur2
    Exemple : Si Situation familiale = Marié(e), Pacsé(e)

  Condition ET - toutes les sous-conditions doivent être vraies simultanément :
    Si [Intitulé A] = ValeurA ET Si [Intitulé B] = ValeurB
    Exemple : Si Situation familiale = Marié(e) ET Si A des enfants = Oui

  Condition OU - au moins une sous-condition doit être vraie :
    Si [Intitulé A] = ValeurA OU Si [Intitulé B] = ValeurB
    Exemple : Si Revenus complémentaires = Chômage OU Si A des enfants = Oui

  Attention : une cellule Condition contenant une virgule (condition simple multi-valeurs)
  doit être entourée de guillemets doubles.

Obligatoire (optionnel)
  Oui ou Non. Par défaut Oui si la colonne est absente ou la cellule vide.

EXEMPLE DE RÉSULTAT ATTENDU :
Intitulé,Question,Type,Description,Options,Condition,Obligatoire
Situation familiale,Quelle est votre situation familiale ?,Choix unique,,"Célibataire, Marié(e), Pacsé(e), Divorcé(e)",,Oui
A des enfants,Avez-vous des enfants à charge ?,Choix unique,,"Oui, Non",,Oui
Revenus complémentaires,Quels revenus complémentaires percevez-vous ?,Choix multiple,,"Retraite, Chômage, RSA",,Oui
Pièce d'identité,Ajoutez votre pièce d'identité en cours de validité,Document,"CNI, passeport ou titre de séjour",,,Oui
Livret de famille,Fournissez votre livret de famille,Document,Acte de mariage ou de PACS,,"Si Situation familiale = Marié(e), Pacsé(e)",Oui
Acte de naissance enfant,,Document,,,Si Situation familiale = Marié(e) ET Si A des enfants = Oui,Oui
Attestation CAF,,Document,,,Si Revenus complémentaires = Chômage OU Si A des enfants = Oui,Oui
Commentaires,,Texte long,Informations complémentaires éventuelles,,,Non

---
Décris maintenant le formulaire que tu souhaites générer, et produis uniquement le contenu CSV sans explication ni bloc de code Markdown.`;

const CODE = ({ children }: { children: string }) => (
  <code className="bg-white border border-neutral-200 px-1 py-0.5 rounded text-neutral-700 text-[11px]">
    {children}
  </code>
);

export function SmartTableFormatGuide() {
  const [showExample, setShowExample] = useState(true);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(COPYABLE_PROMPT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* clipboard access denied - silently ignore */
    }
  };

  return (
    <div className="border border-neutral-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setShowExample((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors text-left"
      >
        <span className="text-sm font-medium text-neutral-700">Exemple de contenu attendu</span>
        {showExample
          ? <ChevronUp className="w-4 h-4 text-neutral-400 shrink-0" />
          : <ChevronDown className="w-4 h-4 text-neutral-400 shrink-0" />
        }
      </button>

      {showExample && (
        <div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-primary-50 border-b border-primary-100">
                  <th className="text-left px-3 py-2 font-semibold text-primary-700 whitespace-nowrap">Intitulé</th>
                  <th className="text-left px-3 py-2 font-semibold text-primary-700 whitespace-nowrap hidden sm:table-cell">Question</th>
                  <th className="text-left px-3 py-2 font-semibold text-primary-700 whitespace-nowrap w-24">Type</th>
                  <th className="text-left px-3 py-2 font-semibold text-primary-700 whitespace-nowrap hidden md:table-cell">Options</th>
                  <th className="text-left px-3 py-2 font-semibold text-primary-700 whitespace-nowrap hidden lg:table-cell">Condition</th>
                  <th className="text-left px-3 py-2 font-semibold text-primary-700 whitespace-nowrap">Obligatoire</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {EXAMPLE_ROWS.map((row, i) => (
                  <tr key={i} className="bg-white hover:bg-primary-50/30 transition-colors">
                    <td className="px-3 py-2 text-neutral-800 font-medium whitespace-nowrap">{row.intitule}</td>
                    <td className="px-3 py-2 text-neutral-500 hidden sm:table-cell max-w-[180px] truncate">
                      {row.question || <span className="text-neutral-300 italic">- (utilise l'intitulé)</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-1.5 py-0.5 bg-neutral-100 rounded text-neutral-600 whitespace-nowrap">
                        {row.type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-neutral-500 hidden md:table-cell max-w-[200px] truncate">
                      {row.options || <span className="text-neutral-300">-</span>}
                    </td>
                    <td className="px-3 py-2 hidden lg:table-cell max-w-[260px] truncate">
                      {row.condition
                        ? <span className="text-primary-600 font-medium">{row.condition}</span>
                        : <span className="text-neutral-300">-</span>
                      }
                    </td>
                    <td className="px-3 py-2 text-neutral-500 whitespace-nowrap">
                      {row.obligatoire === 'Non'
                        ? <span className="text-neutral-400 italic">Non</span>
                        : <span className="text-neutral-500">Oui</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-4 bg-neutral-50 border-t border-neutral-100 space-y-3 text-xs text-neutral-500">
            <div className="flex items-center justify-between gap-3">
              <p className="font-semibold text-neutral-600 text-[11px] uppercase tracking-wide">Spécification du format</p>
              <button
                type="button"
                onClick={handleCopy}
                title="Copier les instructions pour un assistant IA"
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-all ${
                  copied
                    ? 'border-success-300 bg-success-50 text-success-700'
                    : 'border-neutral-200 bg-white text-neutral-600 hover:border-primary-300 hover:text-primary-700 hover:bg-primary-50'
                }`}
              >
                {copied
                  ? <><Check className="w-3.5 h-3.5" />Copié !</>
                  : <><Copy className="w-3.5 h-3.5" />Copier pour un assistant IA</>
                }
              </button>
            </div>

            <div className="space-y-1">
              <p className="font-medium text-neutral-600">Colonnes obligatoires</p>
              <p><CODE>Intitulé</CODE> - Nom court interne du champ. Utilisé comme référence dans la colonne Condition. Ex : <CODE>Situation familiale</CODE></p>
              <p><CODE>Type</CODE> - Type du champ. Valeurs acceptées : <CODE>Document</CODE> <CODE>Texte court</CODE> <CODE>Texte long</CODE> <CODE>Date</CODE> <CODE>Choix unique</CODE> <CODE>Choix multiple</CODE></p>
            </div>

            <div className="space-y-1">
              <p className="font-medium text-neutral-600">Colonnes optionnelles</p>
              <p><CODE>Question</CODE> - Texte affiché au client. Si absent ou vide, l'Intitulé est utilisé. Ex : <CODE>Quelle est votre situation familiale ?</CODE></p>
              <p><CODE>Description</CODE> - Aide ou exemple affiché sous la question. Ex : <CODE>CNI, passeport ou titre de séjour</CODE></p>
              <p><CODE>Options</CODE> - Valeurs séparées par des virgules. Requis pour Choix unique et Choix multiple. Ex : <CODE>Célibataire, Marié(e), Pacsé(e)</CODE></p>
              <p><CODE>Obligatoire</CODE> - <CODE>Oui</CODE> ou <CODE>Non</CODE>. Par défaut <CODE>Oui</CODE> si la colonne est absente ou la cellule vide.</p>
            </div>

            <div className="space-y-1.5">
              <p className="font-medium text-neutral-600">Colonne Condition - syntaxes disponibles</p>
              <p>
                Condition simple (une source, une ou plusieurs valeurs) :{' '}
                <CODE>Si Situation familiale = Marié(e), Pacsé(e)</CODE>
              </p>
              <p>
                Condition ET - toutes les conditions doivent être vraies simultanément :{' '}
                <CODE>Si Situation familiale = Marié(e) ET Si A des enfants = Oui</CODE>
              </p>
              <p>
                Condition OU - au moins une condition doit être vraie :{' '}
                <CODE>Si Revenus complémentaires = Chômage OU Si A des enfants = Oui</CODE>
              </p>
              <p className="text-neutral-400">Le champ source référencé doit être un champ de type Choix unique ou Choix multiple présent dans le même tableau, identifié par son Intitulé exact.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
